import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { X, UserCircle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { BrutalButton } from "@/components/ui/BrutalButton";

export const OnboardingReminder = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [showBanner, setShowBanner] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (user && !dismissed) {
      checkOnboardingStatus();
    }
  }, [user, dismissed]);

  const checkOnboardingStatus = async () => {
    if (!user) return;

    try {
      // Check if user has dismissed this banner in localStorage
      const dismissedKey = `onboarding-reminder-dismissed-${user.id}`;
      if (localStorage.getItem(dismissedKey)) {
        return;
      }

      const { data, error } = await supabase
        .from("user_demographics")
        .select("onboarding_completed, onboarding_skipped")
        .eq("user_id", user.id)
        .maybeSingle();

      if (error) throw error;

      // Show banner if user skipped onboarding or has no record
      if (!data || (data.onboarding_skipped && !data.onboarding_completed)) {
        setShowBanner(true);
      }
    } catch (error) {
      console.error("Error checking onboarding status:", error);
    }
  };

  const handleDismiss = () => {
    if (user) {
      localStorage.setItem(`onboarding-reminder-dismissed-${user.id}`, "true");
    }
    setDismissed(true);
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <div className="bg-primary/10 border-2 border-primary p-4 mb-6 relative">
      <button
        onClick={handleDismiss}
        className="absolute top-2 right-2 p-1 hover:bg-primary/20 rounded"
        aria-label={t("common.close")}
      >
        <X className="h-4 w-4" />
      </button>

      <div className="flex items-start gap-4">
        <UserCircle className="h-8 w-8 text-primary flex-shrink-0" />
        <div className="flex-1">
          <h3 className="font-heading font-bold text-sm mb-1">
            {t("onboarding.reminder.title")}
          </h3>
          <p className="text-sm text-muted-foreground mb-3">
            {t("onboarding.reminder.description")}
          </p>
          <Link to="/dashboard/profile-edit?tab=basic">
            <BrutalButton size="sm" variant="primary">
              {t("onboarding.reminder.cta")}
            </BrutalButton>
          </Link>
        </div>
      </div>
    </div>
  );
};
